import { initializePusher } from "@api/connect";
import { _setActiveAttendance } from "@store/slices/user.slice";
import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

const EVENT_LIST = {
    START_ATTENDANCE: "START_ATTENDANCE",
    END_ATTENDANCE: "END_ATTENDANCE",
};

const usePusherChannel = (channelName = "hr-communication", eventName = "PONG_MESSAGE") => {
    const { userData: user } = useSelector((state) => state?.user);
    const dispatch = useDispatch();

    const handleCallBack = useCallback(
        (message) => {
            let parsedMessage = message;

            if (typeof message === 'string') {
                try {
                    parsedMessage = JSON.parse(message);
                } catch (e) {
                    console.error("Failed to parse message:", e);
                    return;
                }
            }

            if (parsedMessage?.data && typeof parsedMessage.data === 'string') {
                try {
                    parsedMessage = JSON.parse(parsedMessage.data);
                } catch (e) {
                    console.error("Failed to parse data field:", e);
                }
            }

            const { event_name, data } = parsedMessage;

            switch (event_name) {
                case EVENT_LIST.START_ATTENDANCE:
                    if (data?.user_id === user?.id) {
                        dispatch(_setActiveAttendance(Array.isArray(data) ? data : [data]));
                    }
                    break;

                case EVENT_LIST.END_ATTENDANCE:
                    if (data?.user_id === user?.id) {
                        dispatch(_setActiveAttendance([]));
                    }
                    break;

                default:
                    break;
            }
        },
        [dispatch, user?.id]
    );

    useEffect(() => {
        let channel;
        let pusher;
        let connectionHandler;

        try {
            pusher = initializePusher();

            const subscribeToChannel = () => {
                try {
                    if (!pusher) return;

                    channel = pusher.subscribe(channelName);

                    channel.bind('pusher:subscription_succeeded', () => {});

                    channel.bind('pusher:subscription_error', (error) => {
                        console.error(`Subscription error for channel ${channelName}:`, error);
                    });

                    channel.bind(eventName, (pusherData) => {
                        handleCallBack(pusherData);
                    });
                } catch (err) {
                    console.error("Channel subscription error:", err);
                }
            };

            if (pusher.connection.state === 'connected') {
                subscribeToChannel();
            } else {
                connectionHandler = () => {
                    if (pusher.connection.state === 'connected') {
                        subscribeToChannel();
                    }
                };
                pusher.connection.bind('connected', connectionHandler);
            }
        } catch (err) {
            console.error("Pusher initialization error:", err);
        }

        return () => {
            if (pusher && connectionHandler) {
                try {
                    pusher.connection.unbind('connected', connectionHandler);
                } catch (err) {
                    console.error("Error unbinding connection handler:", err);
                }
            }

            if (channel) {
                try {
                    channel.unbind_all();
                    channel.unsubscribe();
                } catch (err) {
                    console.error("Error unsubscribing:", err);
                }
            }
        };
    }, [channelName, eventName, handleCallBack]);

    return {};
};

export default usePusherChannel;
